import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Clock, Mail, Phone, User2, Sparkles } from "lucide-react";

const YEARS = [7, 8, 9, 10, 11];

export default function Signup() {
  const nav = useNavigate();
  const [role, setRole] = useState("parent");
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [submitted, setSubmitted] = useState(null); // payload for thank-you screen
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    branch: "Wokingham",
    student_first_name: "",
    student_last_name: "",
    year_level: 10,
    notes: "",
    captcha_answer: "",
    website: "", // honeypot
  });

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/signup/captcha");
      setCaptcha(data);
    } catch {
      setCaptcha({ token: "", a: 0, b: 0, question: "What is 2 + 2?" });
    }
  };
  useEffect(() => { loadCaptcha(); }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha) return;
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      return toast.error("Please fill every required field.");
    }
    if (form.captcha_answer === "" || isNaN(Number(form.captcha_answer))) {
      return toast.error("Please answer the maths check.");
    }
    setLoading(true);
    try {
      const payload = {
        role,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        branch: form.branch,
        year_level: Number(form.year_level),
        notes: form.notes || null,
        captcha_answer: Number(form.captcha_answer),
        captcha_token: captcha.token,
        website: form.website,
      };
      if (role === "parent") {
        if (!form.student_first_name.trim() || !form.student_last_name.trim()) {
          setLoading(false);
          return toast.error("Please tell us your child's name.");
        }
        payload.student_first_name = form.student_first_name.trim();
        payload.student_last_name = form.student_last_name.trim();
      }
      const { data } = await api.post("/signup/trial", payload);
      setSubmitted({ ...data, role });
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      loadCaptcha();
      setF("captcha_answer", "");
    } finally {
      setLoading(false);
    }
  };

  if (step === 2 && submitted) {
    return <SuccessScreen submitted={submitted}/>;
  }

  return (
    <div className="min-h-screen hero-radial">
      <Navbar/>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-12">
        {/* Form column */}
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">Free trial · No card required</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tighter-x brand-navy mb-4">
            Start your child's<br/>journey to Grade 9.
          </h1>
          <p className="text-slate-600 mb-10 text-lg">
            Fill in a few details and our team will call you within <strong className="brand-navy">24 hours</strong> to arrange a free 45-minute trial lesson at our Wokingham branch (or online).
          </p>

          <div className="flex gap-2 mb-6">
            {[
              { id: "parent", label: "I'm a parent registering my child" },
              { id: "student", label: "I'm a student registering myself" },
            ].map(r => (
              <button key={r.id} type="button" onClick={() => setRole(r.id)} data-testid={`role-${r.id}`}
                className={`flex-1 py-3 px-4 rounded-md text-sm font-medium border text-left transition ${role === r.id ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white brand-navy border-slate-300 hover:border-brand-navy'}`}>
                {r.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 space-y-5">
            {/* Honeypot — hidden, not visible to users */}
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
              value={form.website} onChange={e => setF("website", e.target.value)}
              style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px" }}/>

            <SectionTitle>{role === "parent" ? "Your (parent) details" : "Your details"}</SectionTitle>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="First name" val={form.first_name} set={v => setF("first_name", v)} testid="signup-fname"/>
              <Input label="Last name" val={form.last_name} set={v => setF("last_name", v)} testid="signup-lname"/>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Email" type="email" val={form.email} set={v => setF("email", v)} testid="signup-email" icon={<Mail size={14}/>}/>
              <Input label="Phone" type="tel" val={form.phone} set={v => setF("phone", v)} testid="signup-phone" icon={<Phone size={14}/>}/>
            </div>

            {role === "parent" && (
              <>
                <SectionTitle>Your child's details</SectionTitle>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input label="Child's first name" val={form.student_first_name} set={v => setF("student_first_name", v)} testid="signup-cfname"/>
                  <Input label="Child's last name" val={form.student_last_name} set={v => setF("student_last_name", v)} testid="signup-clname"/>
                </div>
              </>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Year level</Label>
                <select data-testid="signup-year" value={form.year_level} onChange={e => setF("year_level", Number(e.target.value))} className="w-full border border-slate-300 rounded-md px-4 py-3 bg-white text-sm">
                  {YEARS.map(y => <option key={y} value={y}>Year {y}</option>)}
                </select>
              </div>
              <div>
                <Label>Branch</Label>
                <select data-testid="signup-branch" value={form.branch} onChange={e => setF("branch", e.target.value)} className="w-full border border-slate-300 rounded-md px-4 py-3 bg-white text-sm">
                  <option>Wokingham</option>
                  <option>Online</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Anything we should know? (optional)</Label>
              <textarea data-testid="signup-notes" rows={3} value={form.notes} onChange={e => setF("notes", e.target.value)} placeholder="e.g. preferred exam board, specific topics struggling with, preferred class time…" className="w-full border border-slate-300 rounded-md px-4 py-3 bg-white text-sm"/>
            </div>

            <div className="p-4 rounded-md bg-slate-50 border border-slate-200 flex items-end gap-4">
              <div className="flex-1">
                <Label>Quick check: {captcha?.question || "Loading…"}</Label>
                <input data-testid="signup-captcha" type="number" required value={form.captcha_answer} onChange={e => setF("captcha_answer", e.target.value)} className="w-full border border-slate-300 rounded-md px-4 py-3 bg-white text-sm"/>
              </div>
              <button type="button" onClick={loadCaptcha} className="text-xs brand-navy underline mb-4" data-testid="signup-captcha-refresh">Refresh</button>
            </div>

            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-slate-500">By submitting, you agree to our team contacting you by phone or email to arrange your trial lesson.</p>
              <button disabled={loading} className="btn-gold px-8 py-3 rounded-md inline-flex items-center gap-2 shrink-0" data-testid="signup-submit">
                {loading ? "Submitting…" : "Request free trial"} <ArrowRight size={16}/>
              </button>
            </div>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already registered? <Link to="/login" className="brand-navy font-medium hover:gold-text">Sign in to your portal</Link>
          </p>
        </div>

        {/* Side column — reassurance */}
        <aside className="lg:col-span-5 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4"><Sparkles className="gold-text" size={16}/><h4 className="font-serif text-lg brand-navy">What happens next</h4></div>
            <ol className="space-y-4 text-sm">
              <Step n="1" title="You submit this form" body="Takes under a minute. We'll email a confirmation."/>
              <Step n="2" title="Our team calls you within 24 hours" body="We'll understand your child's level, target grade, and availability."/>
              <Step n="3" title="Free 45-minute trial lesson" body="A diagnostic session at our Wokingham branch or online — your choice."/>
              <Step n="4" title="Your personalised plan" body="After the trial, we share a written plan and confirm your place."/>
            </ol>
          </div>

          <div className="panel-navy rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] brand-gold mb-3">Your data</div>
            <p className="text-sm text-white/80 leading-relaxed">Your details are only used to contact you about the trial lesson. We don't sell or share data, and we never spam. Full privacy statement on request.</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-3"><Clock className="gold-text" size={16}/><h4 className="font-serif text-lg brand-navy">Office hours</h4></div>
            <p className="text-sm text-slate-600">Mon–Sat · 9am – 8pm<br/>We usually respond within 2–4 working hours.</p>
          </div>
        </aside>
      </div>
      <Footer/>
    </div>
  );
}

function Input({ label, val, set, type = "text", testid, icon }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>}
        <input required type={type} value={val} onChange={e => set(e.target.value)} className={`w-full border border-slate-300 rounded-md ${icon ? "pl-9" : "pl-4"} pr-4 py-3 bg-white text-sm`} data-testid={testid}/>
      </div>
    </div>
  );
}
function Label({ children }) { return <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{children}</label>; }
function SectionTitle({ children }) { return <h3 className="font-serif text-lg brand-navy border-l-2 border-brand-gold pl-3">{children}</h3>; }
function Step({ n, title, body }) {
  return (
    <li className="flex gap-4">
      <div className="shrink-0 w-8 h-8 rounded-full bg-brand-navy flex items-center justify-center font-serif text-sm" style={{ color: "#D4AF37" }}>{n}</div>
      <div><div className="brand-navy font-semibold">{title}</div><div className="text-slate-600 mt-0.5 text-xs leading-relaxed">{body}</div></div>
    </li>
  );
}

function SuccessScreen({ submitted }) {
  const name = submitted.first_name;
  const childName = submitted.student_first_name || submitted.first_name;
  return (
    <div className="min-h-screen hero-radial">
      <Navbar/>
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="signup-success">
        <div className="w-20 h-20 mx-auto rounded-full bg-brand-navy flex items-center justify-center mb-8" style={{ color: "#D4AF37" }}>
          <CheckCircle2 size={40}/>
        </div>
        <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">Thank you, {name}</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tighter-x brand-navy leading-[1.1] mb-6">
          Your trial request for<br/><em className="italic">{childName}</em> has been received.
        </h1>
        <p className="text-lg text-slate-600 max-w-xl mx-auto mb-12">
          A member of the <strong className="brand-navy">Genius Education</strong> team will be in touch within <strong>24 hours</strong> to arrange your free trial lesson and discuss your child's learning plan.
        </p>

        <div className="bg-white rounded-xl border border-slate-200 p-8 text-left max-w-xl mx-auto mb-10">
          <h3 className="font-serif text-xl brand-navy mb-4">In the meantime</h3>
          <ul className="space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><CheckCircle2 className="gold-text shrink-0" size={18}/>Check your email ({submitted.email}) — we've sent a confirmation.</li>
            <li className="flex gap-3"><CheckCircle2 className="gold-text shrink-0" size={18}/>Keep an eye on your phone ({submitted.phone}) — we'll call from a UK landline.</li>
            <li className="flex gap-3"><CheckCircle2 className="gold-text shrink-0" size={18}/>Explore the free <Link to="/gcse" className="brand-navy underline hover:gold-text">GCSE curriculum map</Link> and our <Link to="/blog" className="brand-navy underline hover:gold-text">journal for parents</Link>.</li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-ghost-navy px-6 py-3 rounded-md" data-testid="success-home">Back to homepage</Link>
          <Link to="/blog" className="btn-gold px-6 py-3 rounded-md" data-testid="success-blog">Read our insights →</Link>
        </div>

        <p className="mt-12 text-xs text-slate-400">Urgent? Email <a href="mailto:hello@geniuseducation.co.uk" className="brand-navy underline">hello@geniuseducation.co.uk</a></p>
      </div>
      <Footer/>
    </div>
  );
}
